import React from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import cn from 'classnames';
import ModalWindow from '../../common/Modal';
import Button from '../../common/Button';
import Switch from '../../common/Switch';
import Price from '../../common/Price';
import styles from './ApproveVariantsModal.module.scss';
import { currencyFormat } from '../../../utils/formatHelper';
import { getDynamicColumn, sortCaret } from '../../../utils/tableHelper';
import { variantSort2 } from '../../../utils/sortHelper';
import { dateFormatter } from '../../../utils/formatHelper';
import { isArray, upperFirst } from 'lodash';
import { TAB_DROPSHIP, TAB_REORDER } from '../../../constants/mainConstants';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { showMSRPPrices } from '../../../constants/featureFlag';

const DISPLAYED_BY_FEATURE_FLAG = [
  'retail_price',
  'special_unit_price',
  'wholesale_price',
  'permissions_special_wsp',
  'permissions_retail_price',
  'permissions_sale_price',
  'permissions_sale_price_start',
  'permissions_sale_price_end',
];
function ApproveVariantsModal(props) {
  const isShowWSRPPricesEnabled = useFeatureFlag(showMSRPPrices, false);
  const {
    productDetails: { variants },
    approve,
    unapprove,
    approved,
    open,
    onClose,
    permissionType,
    shouldAllowUserToDoActions = true,
  } = props;

  const showPricesFilter = (column) => {
    if (DISPLAYED_BY_FEATURE_FLAG.includes(column.dataField)) {
      return isShowWSRPPricesEnabled;
    }

    return true;
  };

  if (!variants) {
    return null;
  }

  const toggleSwitchAll = (value) => {
    if (variants) {
      const variantsIds = variants.map(({ id }) => id);
      toggleSwitch(value, variantsIds);
    }
  };

  const isSwitchedAll = () => {
    return variants.every((variant) => approved.indexOf(variant.id) >= 0);
  };

  const toggleSwitch = (value, id) => {
    const ids = isArray(id) ? id : [id];
    if (value) {
      approve(ids);
    } else {
      unapprove(ids);
    }
  };

  const productInfo = () => {
    const { product } = props;

    return (
      <div className={styles.product}>
        <div className={styles.img_block}>
          <img alt={product.title} src={product.images[0]} />
        </div>

        <div className={styles.description}>
          <p className={styles.pt}>{product.title}</p>

          {isShowWSRPPricesEnabled && (
            <p className={styles.price}>
              <Price
                title="MSRP:"
                max={product.retail_price_max}
                min={product.retail_price_min}
                currency={product.currency}
              />

              {product.wholesale_price_max && (
                <Price
                  title="WSP:"
                  max={product.wholesale_price_max}
                  min={product.wholesale_price_min}
                  currency={product.currency}
                />
              )}
            </p>
          )}
        </div>

        <div className={styles.details}>
          <p>
            {product.available_inventory} units - {product.variants.length} variants
          </p>
          <p>Approved to {product.retailers?.length || 0} retailers</p>
          <p className={styles.model}>Model: {product.model}</p>
        </div>
      </div>
    );
  };

  const saveModal = () => {
    props.saveVariants();
    onClose();
  };

  const formatStatus = (cell, row, rowIndex, { approved, toggleSwitch }) => {
    const isSwitched = approved.includes(row.id);
    return <Switch checked={isSwitched} onSwitch={(value) => toggleSwitch(value, row.id)} />;
  };

  const formatCurrency = (cell, row) => currencyFormat(cell, row.currency);

  const formatDynamicColumn = (cell, row, rowIndex, field) => {
    return row.options[field] ? <span>{row.options[field]}</span> : <span>-</span>;
  };

  const formatPrice = (cell, row, rowIndex, extraData) => {
    const { field } = extraData;
    let price;
    const permissions = row.permissions;
    if (permissions && permissions.length && permissions[0][field]) {
      price = permissions[0][field];
    } else if (extraData.getFromVariant) {
      price = row[field];
    }
    return price ? formatCurrency(price, row) : <span>-</span>;
  };

  const formatSalePrice = (cell, row) => {
    const permissions = row.permissions;
    if (permissions && permissions.length && permissions[0].special_sale_price) {
      return formatCurrency(permissions[0].special_sale_price, row);
    } else {
      return <span>-</span>;
    }
  };

  const formatDate = (cell, row, rowIndex, field) => {
    const permissions = row.permissions;
    if (permissions && permissions.length && permissions[0][field]) {
      return dateFormatter(permissions[0][field]);
    } else {
      return <span>-</span>;
    }
  };

  const columns = [];
  const hasPermissions = variants.some((variant) => variant.permissions && variant.permissions.length > 0);
  const hasUpc = variants.some((variant) => !!variant.upc);
  const hasEan = variants.some((variant) => !!variant.ean);
  const dynamicColumns = getDynamicColumn(variants);

  const addPermissionColumns = () => {
    if (permissionType === TAB_DROPSHIP) {
      columns.push({
        text: 'MSRP',
        dataField: 'retail_price',
        formatter: formatCurrency,
      });

      if (props.showSpecialUnitPrice) {
        columns.push({
          text: 'Special Unit Price',
          dataField: 'special_unit_price',
          formatter: formatPrice,
          formatExtraData: { field: 'special_unit_price' },
        });
      }

      if (hasPermissions) {
        columns.push(
          {
            text: 'Special Retail Price',
            dataField: 'permissions_retail_price',
            formatter: formatPrice,
            formatExtraData: { field: 'retail_price' },
          },
          {
            text: 'Sale Price',
            dataField: 'permissions_sale_price',
            formatter: formatSalePrice,
          },
          {
            text: 'Sale Price Start',
            dataField: 'permissions_sale_price_start',
            formatter: formatDate,
            formatExtraData: 'special_sale_price_start',
          },
          {
            text: 'Sale Price End',
            dataField: 'permissions_sale_price_end',
            formatter: formatDate,
            formatExtraData: 'special_sale_price_end',
          },
        );
      }
    } else if (permissionType === TAB_REORDER) {
      columns.push({
        text: 'WSP',
        dataField: 'wholesale_price',
        formatter: formatCurrency,
      });

      if (hasPermissions) {
        columns.push({
          text: 'Special WSP',
          dataField: 'permissions_special_wsp',
          formatter: formatPrice,
          formatExtraData: { field: 'wholesale_price', getFromVariant: true },
        });
      }
    }
  };
  if (shouldAllowUserToDoActions) {
    columns.push({
      text: 'Id',
      dataField: 'id',
      formatter: formatStatus,
      formatExtraData: {
        approved,
        toggleSwitch,
      },
      headerFormatter: () => <Switch checked={isSwitchedAll()} onSwitch={toggleSwitchAll} />,
      isDummyField: true,
      classes: 'narrow',
      headerClasses: 'narrow',
    });
  }
  dynamicColumns.forEach((field, i) => {
    columns.push({
      text: upperFirst(field),
      dataField: field,
      formatter: formatDynamicColumn,
      formatExtraData: field,
      sort: true,
    });
  });

  columns.push(
    {
      text: 'Stock',
      dataField: 'available_inventory',
      sort: true,
    },
    {
      text: 'SKU',
      dataField: 'sku',
    },
  );

  if (hasUpc) {
    columns.push({
      text: 'UPC',
      dataField: 'upc',
      sort: true,
    });
  }

  if (hasEan) {
    columns.push({
      text: 'EAN',
      dataField: 'ean',
      sort: true,
    });
  }

  addPermissionColumns();

  const wrapperClasses = [styles.table, styles[`table-${permissionType}`]];

  if (permissionType === TAB_DROPSHIP && hasPermissions) {
    wrapperClasses.push(styles.tableWide);
  }

  return (
    <ModalWindow open={open} onClose={onClose} size="xlarge">
      <h2>{shouldAllowUserToDoActions ? `Approve Variants` : `Product Details`}</h2>
      <hr />

      <div className={styles.body}>
        {productInfo()}

        <div className={cn(wrapperClasses)}>
          <BootstrapTable
            data={variants}
            columns={columns.filter(showPricesFilter)}
            keyField="id"
            printable
            striped
            hover
            bordered={false}
            defaultSorted={[
              {
                dataField: dynamicColumns[0] || 'available_inventory',
                order: 'asc',
              },
            ]}
            defaultSortDirection="asc"
            sort={{
              sortCaret: sortCaret,
              sortFunc: variantSort2,
            }}
          ></BootstrapTable>
        </div>
      </div>
      <hr />

      {shouldAllowUserToDoActions ? (
        <div className={styles.footer}>
          <Button color="white" text="CANCEL" onClick={onClose} />

          <Button text="SAVE" onClick={saveModal} disabled={!approved.length} />
        </div>
      ) : (
        <></>
      )}
    </ModalWindow>
  );
}

export default ApproveVariantsModal;

ApproveVariantsModal.defaultProps = {
  open: false,
  permissionType: undefined,
};

ApproveVariantsModal.propTypes = {
  approved: PropTypes.arrayOf(PropTypes.number).isRequired,
  product: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  productDetails: PropTypes.object.isRequired,
  approve: PropTypes.func.isRequired,
  unapprove: PropTypes.func.isRequired,
  saveVariants: PropTypes.func.isRequired,
  permissionType: PropTypes.oneOf([TAB_DROPSHIP, TAB_REORDER]),
};
